import {zodResolver} from "@hookform/resolvers/zod";
import {
  type ICompany,
  RequiredApeCodeSchema,
  type UpdateLegalNumbersPayload,
} from "@mytour/shared";
import {Splitter} from "@mytour/ui/layout";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {Button, Form, FormFeedback, FormGroup, Input, Label, Spinner} from "reactstrap";
import {z} from "zod";
import {useUpdateLegalNumbers} from "../../../query/companySettingsQueries";

type Props = {
  company: ICompany;
};

const legalNumbersSchema = z
  .object({
    siret: z.string().trim().min(1, "Champ obligatoire"),
    apeCode: RequiredApeCodeSchema,
    apeLabel: z.string().min(1, "Champ obligatoire"),
    rcsNumber: z.string(),
    vatId: z.string(),
  })
  .superRefine((data, ctx) => {
    if (!/^[0-9]{14}$/.test(data.siret)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["siret"],
        message: "Le SIRET doit contenir exactement 14 chiffres",
      });
    }
  });

type LegalNumbersFormData = z.infer<typeof legalNumbersSchema>;

export function LegalNumbersSubTab({company}: Props) {
  const {t: translate} = useTranslation();
  const {updateLegalNumbers, isUpdatingLegalNumbers} = useUpdateLegalNumbers();
  const disabled = isUpdatingLegalNumbers;

  const {
    register,
    handleSubmit,
    watch,
    formState: {errors},
  } = useForm<LegalNumbersFormData>({
    resolver: zodResolver(legalNumbersSchema),
    defaultValues: {
      siret: company.legal.siret ?? "",
      apeCode: company.legal.apeCode ?? "",
      apeLabel: company.legal.apeLabel ?? "",
      rcsNumber: company.legal.rcsNumber ?? "",
      vatId: company.legal.vatId ?? "",
    },
  });

  const vatId = watch("vatId");

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup>
        <Label for="siret">{translate("myTourApp.platformUser.siret")} *</Label>
        <Input
          id="siret"
          innerRef={register("siret").ref}
          onChange={register("siret").onChange}
          onBlur={register("siret").onBlur}
          name="siret"
          invalid={!!errors.siret}
        />
        {errors.siret && <FormFeedback>{errors.siret.message}</FormFeedback>}
      </FormGroup>

      <Splitter width1={30} width2={68}>
        <FormGroup>
          <Label for="apeCode">{translate("myTourApp.platformUser.apeCode")} *</Label>
          <Input
            id="apeCode"
            innerRef={register("apeCode").ref}
            onChange={register("apeCode").onChange}
            onBlur={register("apeCode").onBlur}
            name="apeCode"
            invalid={!!errors.apeCode}
          />
          {errors.apeCode && <FormFeedback>{errors.apeCode.message}</FormFeedback>}
        </FormGroup>
        <FormGroup>
          <Label for="apeLabel">{translate("myTourApp.platformUser.apeLabel")} *</Label>
          <Input
            id="apeLabel"
            innerRef={register("apeLabel").ref}
            onChange={register("apeLabel").onChange}
            onBlur={register("apeLabel").onBlur}
            name="apeLabel"
            invalid={!!errors.apeLabel}
          />
          {errors.apeLabel && <FormFeedback>{errors.apeLabel.message}</FormFeedback>}
        </FormGroup>
      </Splitter>

      <FormGroup>
        <Label for="rcsNumber">{translate("myTourApp.platformUser.rcsNumber")}</Label>
        <Input
          id="rcsNumber"
          innerRef={register("rcsNumber").ref}
          onChange={register("rcsNumber").onChange}
          onBlur={register("rcsNumber").onBlur}
          name="rcsNumber"
        />
      </FormGroup>

      <FormGroup>
        <Label for="vatId">{translate("myTourApp.platformUser.vatId")}</Label>
        <Input
          id="vatId"
          innerRef={register("vatId").ref}
          onChange={register("vatId").onChange}
          onBlur={register("vatId").onBlur}
          name="vatId"
        />
        {vatId && (
          <div className="alert alert-info mt-2">
            <p>
              <strong>Un numéro de TVA est renseigné.</strong>
              <br />
              TVA à 20% pour les prochaines factures
            </p>
          </div>
        )}
      </FormGroup>

      <Button color="primary" type="submit" disabled={disabled}>
        {isUpdatingLegalNumbers ? <Spinner size="sm" /> : null}
        {isUpdatingLegalNumbers ? " " : ""}Sauvegarder
      </Button>
    </Form>
  );

  async function onSubmit(data: LegalNumbersFormData) {
    const payload: UpdateLegalNumbersPayload = {
      siret: data.siret.trim(),
      apeCode: data.apeCode,
      apeLabel: data.apeLabel,
      rcsNumber: data.rcsNumber,
      vatId: data.vatId,
    };

    try {
      await updateLegalNumbers(payload);
      toast.success("Numéros légaux enregistrés");
    } catch {
      toast.error("Erreur lors de la sauvegarde des numéros légaux");
    }
  }
}
